'use client';

import { useEffect, useRef, useState } from 'react';
import type {
  LostSwordDialogueBranch,
  LostSwordDialogueGroup,
} from '../lib/heroes/types';
import { staticUrl } from '../lib/config/static-assets';

const UI = staticUrl('/assets/lostsword-ui');

const FAVOR_META = {
  excellent: { icon: `${UI}/archive-excellent.png`, num: 70 },
  good: { icon: `${UI}/archive-good.png`, num: 50 },
  bad: { icon: `${UI}/archive-bad.png`, num: 0 },
} as const;

export function LostSwordVoicePanel({
  groups,
}: {
  groups: [string, { text: string; src?: string }[]][];
}) {
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!playingKey) return;
    const audio = audioRefs.current[playingKey];
    if (!audio) return;
    let frame = 0;
    const tick = () => {
      if (!audio.duration || Number.isNaN(audio.duration)) {
        setProgress(0);
      } else {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
      if (!audio.paused && !audio.ended) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    const onEnded = () => {
      setPlayingKey(null);
      setProgress(0);
    };
    audio.addEventListener('ended', onEnded);
    return () => {
      cancelAnimationFrame(frame);
      audio.removeEventListener('ended', onEnded);
    };
  }, [playingKey]);

  const handlePlay = (key: string) => {
    const audio = audioRefs.current[key];
    if (!audio?.src) return;

    if (playingKey && playingKey !== key) {
      const prev = audioRefs.current[playingKey];
      if (prev) {
        prev.pause();
        prev.currentTime = 0;
      }
      setProgress(0);
    }

    if (playingKey === key && !audio.paused) {
      audio.pause();
      setPlayingKey(null);
      return;
    }

    void audio.play();
    setPlayingKey(key);
  };

  return (
    <div className="voice">
      {groups.map(([label, items], groupIndex) => (
        <div className="voice-group" key={label}>
          <div className="voice-title">【{label}】</div>
          {items.map((item, index) => {
            const key = `${groupIndex}-${index}`;
            const isPlaying = playingKey === key;
            return (
              <div className="voice-list" key={key}>
                <div
                  className="list-item"
                  onClick={() => handlePlay(key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handlePlay(key);
                    }
                  }}
                >
                  <div className="item-text">{item.text}</div>
                  <img
                    src={
                      isPlaying
                        ? `${UI}/archive-pause.png`
                        : `${UI}/archive-play.png`
                    }
                    alt=""
                    className="item-btn"
                  />
                  <div
                    className="progress-bar"
                    style={{ width: isPlaying ? `${progress}%` : '0%' }}
                  />
                  {item.src ? (
                    <audio
                      ref={(node) => {
                        audioRefs.current[key] = node;
                      }}
                      src={item.src}
                      preload="auto"
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function FavorBadge({ favor }: { favor?: LostSwordDialogueBranch['favor'] }) {
  if (!favor) return null;
  const meta = FAVOR_META[favor];
  if (!meta) return null;
  return (
    <div className="favorability-box">
      <div className="favorability-item">
        <img src={meta.icon} alt="" />
        <div className="favorability-num">{meta.num}</div>
      </div>
    </div>
  );
}

function DialogueBranchItem({
  branch,
  roleAvatar,
  expandKey,
}: {
  branch: LostSwordDialogueBranch;
  roleAvatar: string;
  expandKey: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="branch-item">
      <div
        className="item-header"
        onClick={() => setExpanded((value) => !value)}
      >
        <div className="header-left">
          <img
            src={`${UI}/archive-player-avator.png`}
            alt=""
            className="player-image"
          />
          <div className="player-lines">{branch.choice}</div>
        </div>
        <div className="header-right">
          <FavorBadge favor={branch.favor} />
          <img
            src={`${UI}/archive-arrow.png`}
            alt=""
            className={`arrow${expanded ? ' rotate' : ''}`}
          />
        </div>
      </div>
      {expanded ? (
        <div className="item-content">
          {branch.replies.map((line, index) => (
            <div className="content-lines" key={`${expandKey}-${index}`}>
              <img src={roleAvatar} alt="" className="role-image" />
              <div className="lines-text">{line}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function LostSwordDialoguePanel({
  groups,
  roleAvatar,
}: {
  groups: LostSwordDialogueGroup[];
  roleAvatar: string;
}) {
  if (!groups.length) return null;
  return (
    <div className="dialogue">
      {groups.map((group, index) => (
        <div className="dialogue-group" key={`dialogue-${index}`}>
          {group.opening ? (
            <div className="opening">
              <img src={roleAvatar} alt="" className="role-image" />
              <div className="lines">{group.opening}</div>
            </div>
          ) : null}
          {group.branches.length > 0 ? (
            <div className="dialogue-branch">
              {group.branches.map((branch, branchIndex) => (
                <DialogueBranchItem
                  key={`branch-${index}-${branchIndex}`}
                  branch={branch}
                  roleAvatar={roleAvatar}
                  expandKey={`branch-${index}-${branchIndex}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
